import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

export interface PriceTableRow {
  recId: number;
  rateType: string | null;
  rangeFrom: number;
  rangeTo: number;
  rate1: number | null; rate2: number | null; rate3: number | null;
  rate4: number | null; rate5: number | null; rate6: number | null;
  rate7: number | null; rate8: number | null; rate9: number | null;
  rangeDesc: string | null;
  sortOrder: number;
  rowVersion: string | null;
}
export interface PriceTableListItem { priceTableId: number; description: string; rowCount: number; rateTypesUsed: string | null; rowVersion: string | null; }
export interface PriceTableDetail { priceTableId: number; description: string; rowVersion: string | null; rows: PriceTableRow[]; }
export interface PriceTableAdminPage { items: PriceTableListItem[]; total: number; page: number; pageSize: number; }
export interface CreatePriceTableBody { description: string; }
export type UpdatePriceTableBody = CreatePriceTableBody;
export interface UpsertRowBody {
  rateType: string | null; rangeFrom: number; rangeTo: number;
  rate1: number | null; rate2: number | null; rate3: number | null;
  rate4: number | null; rate5: number | null; rate6: number | null;
  rate7: number | null; rate8: number | null; rate9: number | null;
  rangeDesc: string | null; sortOrder: number;
}
export interface ReplaceRowsBody { rows: UpsertRowBody[]; }
export interface TestPriceBody { quantity: number; rateAxis: string; rateColumn: number | null; colours: number | null; positions: number | null; rateTypeCode: string | null; }
export interface TestPriceResult { matchedRecId: number | null; rangeDesc: string | null; rateColumn: number; value: number | null; }
export interface PriceTableTemplate { priceTableTemplateId: number; priceTableId: number; description: string | null; }

export const priceTablesAdminApi = {
  listAdmin: (q: string, page = 0, pageSize = 50): Promise<PriceTableAdminPage> =>
    apiFetch<PriceTableAdminPage>(`/api/price-tables?page=${page}&pageSize=${pageSize}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  get: (id: number): Promise<PriceTableDetail> =>
    apiFetch<PriceTableDetail>(`/api/price-tables/${id}`),
  create: (body: CreatePriceTableBody): Promise<PriceTableDetail> =>
    apiFetch<PriceTableDetail>("/api/price-tables", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: UpdatePriceTableBody, rowVersion: string | null): Promise<PriceTableDetail> =>
    apiFetch<PriceTableDetail>(`/api/price-tables/${id}`, { method: "PATCH", body: JSON.stringify(body), headers: rowVersion ? { "If-Match": rowVersion } : {} }),
  remove: (id: number, rowVersion: string | null): Promise<void> =>
    apiFetch<void>(`/api/price-tables/${id}`, { method: "DELETE", headers: rowVersion ? { "If-Match": rowVersion } : {} }),
  replaceRows: (id: number, body: ReplaceRowsBody): Promise<void> =>
    apiFetch<void>(`/api/price-tables/${id}/rows`, { method: "PUT", body: JSON.stringify(body) }),
  test: (id: number, body: TestPriceBody): Promise<TestPriceResult> =>
    apiFetch<TestPriceResult>(`/api/price-tables/${id}/test`, { method: "POST", body: JSON.stringify(body) }),
};

export const priceTableTemplatesApi = {
  list: (): Promise<PriceTableTemplate[]> => apiFetch<PriceTableTemplate[]>("/api/price-table-templates"),
};

export interface PriceTableValidationFinding {
  priceTableId: number;
  priceTableDescription: string;
  rowRecId: number | null;
  ruleCode: string;
  message: string;
}
export interface PriceTableValidation {
  checkedAtUtc: string | null;
  findings: PriceTableValidationFinding[];
}

export const priceTableValidationApi = {
  get: (): Promise<PriceTableValidation> =>
    apiFetch<PriceTableValidation>("/api/price-tables/validation"),
};

export const priceTableKeys = {
  adminList: (q: string, page: number) => ["price-tables", "admin", page, q] as const,
  detail: (id: number) => ["price-tables", "detail", id] as const,
  templates: () => ["price-table-templates"] as const,
  validation: () => ["price-tables", "validation"] as const,
};

export function usePriceTablesAdmin(q: string, page = 0) {
  return useQuery({ queryKey: priceTableKeys.adminList(q, page), queryFn: () => priceTablesAdminApi.listAdmin(q, page) });
}
export function usePriceTableDetail(id: number) {
  return useQuery({ queryKey: priceTableKeys.detail(id), queryFn: () => priceTablesAdminApi.get(id), enabled: id > 0 });
}
